import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import {
  clearMarketingReport,
  readMarketingReport,
  type MarketingBatch,
} from '@/lib/marketing-report';
import { cn } from '@/lib/utils';

const AUDIENCE_LABEL: Record<string, string> = {
  REGISTERED: 'Registered',
  UNREGISTERED: 'Unregistered',
  ALL_MEMBERS: 'All members',
};

export function StoredMarketingReport({ eventId }: { eventId: string }) {
  const [batch, setBatch] = useState<MarketingBatch | null>(null);

  useEffect(() => {
    setBatch(readMarketingReport(eventId));
  }, [eventId]);

  if (!batch) return null;

  return (
    <MarketingReportCard
      batch={batch}
      onClose={() => {
        clearMarketingReport();
        setBatch(null);
      }}
    />
  );
}

export function MarketingReportCard({
  batch,
  onClose,
  className,
}: {
  batch: MarketingBatch;
  onClose: () => void;
  className?: string;
}) {
  const audience = AUDIENCE_LABEL[batch.audience] || batch.audience || 'Selected people';
  const counts = batch.templateMissing
    ? 'Nothing was sent'
    : batch.queued !== undefined && batch.sent + batch.failed === 0
      ? `Queued ${batch.queued}`
      : `${batch.sent} sent${batch.failed ? `, ${batch.failed} failed` : ''}`;

  return (
    <motion.div
      initial={{ y: 24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: 'spring', damping: 24, stiffness: 260 }}
      className={cn(
        'fixed bottom-24 left-4 right-4 z-[60] mx-auto max-w-md rounded-xl border border-border bg-card p-4 shadow-lg shadow-primary/20 md:bottom-24 md:left-auto md:right-6',
        className
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-semibold text-foreground">{batch.eventTitle || 'Event'}</p>
          <p className="text-xs text-muted-foreground">
            {[batch.eventStartDate, batch.eventLocation].filter(Boolean).join(' · ')}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg p-1 text-muted-foreground hover:bg-muted"
          aria-label="Close report"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <p className="mt-3 text-sm text-foreground">{batch.reason}</p>
      <div className="mt-3 flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>{audience}</span>
        <span className="font-medium text-foreground">{counts}</span>
      </div>
    </motion.div>
  );
}
